/**
 * File: effortService.ts
 * Path: apps/admin/src/shared/services/effortService.ts
 * Description: Reads and writes time entries, rand costs and the shared
 * monthly costs. Entries are written once and can be removed, never edited
 * (firestore.rules). The write side takes its Firestore instance from a
 * getter so the rules tests can run these exact writes against the emulator.
 */

import {
  db,
  FIRESTORE_COLLECTIONS,
  timeCostCents,
  toCostEntry,
  toMonthlyCosts,
  toTimeEntry,
  type CostEntry,
  type CostKind,
  type EffortRecordKind,
  type MonthlyCosts,
  type TimeEntry,
  type TimeKind,
} from "@idk/config";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
  addDoc,
  type Firestore,
} from "firebase/firestore";

const TIME = FIRESTORE_COLLECTIONS.timeEntries;
const COSTS = FIRESTORE_COLLECTIONS.costEntries;
const MONTHLY = FIRESTORE_COLLECTIONS.crmCosts;

/** Who is doing the work, stamped on every entry. */
export interface Worker {
  uid: string;
  email: string;
}

/** The lead or client an entry belongs to. */
export interface EffortRecord {
  kind: EffortRecordKind;
  id: string;
  name: string;
  /** The client job the time is for (time only), when it is for one. */
  jobId?: string;
}

export interface NewTime {
  record: EffortRecord;
  minutes: number;
  kind: TimeKind;
  note: string;
  /** YYYY-MM-DD */
  date: string;
  /** Hourly rate in cents for the person working, read from settings. */
  rateCents: number;
}

export interface NewCost {
  record: EffortRecord;
  amountCents: number;
  kind: CostKind;
  note: string;
  date: string;
}

const newestFirst = <T extends { date: string; createdAt: number | null }>(rows: T[]): T[] =>
  [...rows].sort((a, b) => b.date.localeCompare(a.date) || (b.createdAt ?? 0) - (a.createdAt ?? 0));

export const createEffortRepo = (getDb: () => Firestore) => ({
  time: async (kind: EffortRecordKind, id: string): Promise<TimeEntry[]> => {
    const snap = await getDocs(
      query(collection(getDb(), TIME), where("recordKind", "==", kind), where("recordId", "==", id)),
    );
    return newestFirst(
      snap.docs.flatMap((d) => {
        const entry = toTimeEntry(d.id, d.data());
        return entry ? [entry] : [];
      }),
    );
  },

  /** Every time entry dated on or after `from` (YYYY-MM-DD), for the Numbers page. */
  timeSince: async (from: string): Promise<TimeEntry[]> => {
    const snap = await getDocs(query(collection(getDb(), TIME), where("date", ">=", from)));
    return snap.docs.flatMap((d) => {
      const entry = toTimeEntry(d.id, d.data());
      return entry ? [entry] : [];
    });
  },

  costsSince: async (from: string): Promise<CostEntry[]> => {
    const snap = await getDocs(query(collection(getDb(), COSTS), where("date", ">=", from)));
    return snap.docs.flatMap((d) => {
      const entry = toCostEntry(d.id, d.data());
      return entry ? [entry] : [];
    });
  },

  costs: async (kind: EffortRecordKind, id: string): Promise<CostEntry[]> => {
    const snap = await getDocs(
      query(collection(getDb(), COSTS), where("recordKind", "==", kind), where("recordId", "==", id)),
    );
    return newestFirst(
      snap.docs.flatMap((d) => {
        const entry = toCostEntry(d.id, d.data());
        return entry ? [entry] : [];
      }),
    );
  },

  addTime: async (input: NewTime, worker: Worker): Promise<void> => {
    await addDoc(collection(getDb(), TIME), {
      recordKind: input.record.kind,
      recordId: input.record.id,
      recordName: input.record.name.slice(0, 200),
      ...(input.record.jobId ? { jobId: input.record.jobId } : {}),
      minutes: input.minutes,
      kind: input.kind,
      note: input.note.trim().slice(0, 300),
      date: input.date,
      byUid: worker.uid,
      byEmail: worker.email,
      rateCents: input.rateCents,
      costCents: timeCostCents(input.minutes, input.rateCents),
      createdAt: serverTimestamp(),
    });
  },

  addCost: async (input: NewCost, worker: Worker): Promise<void> => {
    await addDoc(collection(getDb(), COSTS), {
      recordKind: input.record.kind,
      recordId: input.record.id,
      recordName: input.record.name.slice(0, 200),
      amountCents: input.amountCents,
      kind: input.kind,
      note: input.note.trim().slice(0, 300),
      date: input.date,
      byUid: worker.uid,
      byEmail: worker.email,
      createdAt: serverTimestamp(),
    });
  },

  removeTime: (id: string) => deleteDoc(doc(getDb(), TIME, id)),
  removeCost: (id: string) => deleteDoc(doc(getDb(), COSTS, id)),

  /** Every entry of a record goes with it when the record is deleted. */
  removeAllFor: async (kind: EffortRecordKind, id: string): Promise<void> => {
    for (const name of [TIME, COSTS]) {
      const snap = await getDocs(
        query(collection(getDb(), name), where("recordKind", "==", kind), where("recordId", "==", id)),
      );
      await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
    }
  },

  monthly: async (month: string): Promise<MonthlyCosts> => {
    const snap = await getDoc(doc(getDb(), MONTHLY, month));
    return toMonthlyCosts(month, snap.exists() ? snap.data() : null);
  },

  saveMonthly: async (costs: MonthlyCosts): Promise<void> => {
    await setDoc(doc(getDb(), MONTHLY, costs.month), {
      items: costs.items,
      adSpend: costs.adSpend,
      updatedAt: serverTimestamp(),
    });
  },
});

export const effortRepo = createEffortRepo(() => db);
export type EffortRepo = ReturnType<typeof createEffortRepo>;
